// IM-XPR-003 (IM-UI-100, IM-UX-116) — Análises e dados: as engrenagens. Cada capacidade existente com uma linha de
// finalidade, uma situação útil (lida das rotas de sempre; sem contador zerado: ausência vira orientação) e a ação de
// abrir a mesma tela de antes. Os frameworks continuam todos disponíveis; só saíram do menu principal.
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { battlecardApi } from '../data/battlecards';
import { environmentApi } from '../data/environment';
import { decisionsApi } from '../data/executive';
import { intelApi } from '../data/intel';
import { learningApi } from '../data/learning';
import { marketApi } from '../data/market';
import { portfolioApi } from '../data/portfolio';
import { scenariosApi } from '../data/scenarios';
import { segmentationApi } from '../data/segmentation';
import { sizingApi } from '../data/sizing';
import { sourceRecoApi } from '../data/sources';
import { signalsAdapter } from '../data/adapters';
import { useResource } from '../hooks/useResource';
import { CAPABILITY_GROUPS, type CapabilityStatusKey } from './catalog';
import { loadCompetitorFacts, loadTargetFacts, plural } from './data';

type Status = Partial<Record<CapabilityStatusKey, string>>;
const settle = async <T,>(p: Promise<T>): Promise<T | null> => p.catch(() => null);

/** Situação útil de cada capacidade (frase curta; nunca "0 …" sozinho). */
async function loadStatus(signal: AbortSignal): Promise<Status> {
  const [comp, market, seg, sizing, env, cards, pf, tg, dec, scn, lrn, src, pages, sig, sched] = await Promise.all([
    settle(loadCompetitorFacts(signal)), settle(marketApi.list({ archived: 'exclude' }, signal)), settle(segmentationApi.list(signal)), settle(sizingApi.list({}, signal)),
    settle(environmentApi.list(signal)), settle(battlecardApi.list(signal)), settle(portfolioApi.list(signal)), settle(loadTargetFacts(signal)), settle(decisionsApi.summary(signal)),
    settle(scenariosApi.summary(signal)), settle(learningApi.summary(signal)), settle(sourceRecoApi.overview(signal)), settle(intelApi.pages('exclude', signal)),
    settle(signalsAdapter.summary(signal)), settle(intelApi.schedules(signal)),
  ]);
  const n = (count: number | undefined | null, one: string, many: string, none: string) => (count ? plural(count, one, many) : none);
  const activeCards = cards?.items.filter((x) => x.battlecard?.status === 'ACTIVE') ?? [];
  const pending = activeCards.reduce((k, x) => k + (x.battlecard?.pending ?? 0), 0);
  return {
    competitors: comp ? (market?.counts.active ? `${plural(market.counts.active, 'análise de mercado', 'análises de mercado')} · ${n(comp.confirmed, 'concorrente confirmado', 'concorrentes confirmados', 'nenhum concorrente confirmado')}` : n(comp.confirmed, 'concorrente confirmado', 'concorrentes confirmados', 'Comece por uma análise de mercado')) : undefined,
    segmentation: seg ? n(seg.items.length, 'análise de segmentação', 'análises de segmentação', 'Nenhuma análise ainda') : undefined,
    sizing: sizing ? n(sizing.items.length, 'estimativa', 'estimativas', 'Nenhuma estimativa ainda') : undefined,
    environment: env ? n(env.items.length, 'análise de ambiente', 'análises de ambiente', 'Nenhuma análise ainda') : undefined,
    battlecards: cards ? (activeCards.length ? `${plural(activeCards.length, 'dossiê ativo', 'dossiês ativos')}${pending ? ` · ${plural(pending, 'ponto para revisar', 'pontos para revisar')}` : ''}` : 'Nenhum dossiê ainda') : undefined,
    portfolio: pf ? n(pf.items.filter((x) => x.status === 'ACTIVE').length, 'análise de portfólio', 'análises de portfólio', 'Nenhuma análise ainda') : undefined,
    executive: dec ? (dec.total ? 'Gerado a partir das análises' : 'Gere quando houver análises') : undefined,
    targets: tg ? (tg.targets ? `${plural(tg.targets, 'empresa', 'empresas')}${tg.targetsReview ? ` · ${tg.targetsReview} para avaliar` : ''}` : 'Nenhuma empresa ainda') : undefined,
    people: tg ? (tg.people ? `${plural(tg.people, 'pessoa', 'pessoas')}${tg.peopleReview ? ` · ${tg.peopleReview} para conferir` : ''}` : 'Aparecem depois das empresas aprovadas') : undefined,
    decisions: dec ? (dec.total ? `${plural(dec.total, 'decisão', 'decisões')}${dec.attention ? ` · ${dec.attention} pedem atenção` : ''}` : 'Nenhuma decisão ainda') : undefined,
    scenarios: scn ? n(scn.total, 'simulação', 'simulações', 'Nenhuma simulação ainda') : undefined,
    learning: lrn ? (lrn.hasReport ? `Relatório versão ${lrn.versionNo}` : 'Nenhum resultado registrado ainda') : undefined,
    sources: src ? (src.active ? `${plural(src.active, 'fonte ativa', 'fontes ativas')}${src.recommended ? ` · ${src.recommended} recomendadas` : ''}${src.problems ? ` · ${src.problems} com problema` : ''}` : src.recommended ? `${plural(src.recommended, 'fonte recomendada', 'fontes recomendadas')} para ativar` : 'Nenhuma fonte ainda') : undefined,
    pages: pages ? n(pages.items.length, 'página acompanhada', 'páginas acompanhadas', 'Ative páginas recomendadas') : undefined,
    signals: sig ? n(sig.total, 'sinal verificável', 'sinais verificáveis', 'Nenhum sinal verificável ainda') : undefined,
    schedules: sched ? n(sched.items.length, 'agenda', 'agendas', 'Nenhuma agenda ainda') : undefined,
    overview: 'Todos os indicadores, como antes',
  };
}

export function AnalysesHub() {
  useEffect(() => { document.title = 'Análises e dados · InteliMove'; }, []);
  const { state } = useResource((s) => loadStatus(s), []);
  const status: Status = state.kind === 'ready' ? state.data : {};
  return (
    <section aria-labelledby="page-title">
      <header className="xp-head">
        <p className="xp-eyebrow">Apoio e transparência</p>
        <h1 id="page-title">Análises e dados</h1>
        <p className="xp-lead">Aqui ficam as engrenagens. Elas sustentam as recomendações, mas não atrapalham o caminho principal.</p>
      </header>
      <div className="xp-hub" aria-busy={state.kind === 'loading'}>
        {CAPABILITY_GROUPS.map((g) => (
          <section key={g.key} aria-labelledby={`hub-${g.key}`}>
            <h2 id={`hub-${g.key}`}>{g.title}</h2>
            <ul role="list">
              {g.items.map((c) => (
                <li key={c.to}>
                  <Link className="xp-card" to={c.to} aria-label={`Abrir ${c.name}`}>
                    <h3>{c.name}</h3>
                    <p>{c.purpose}</p>
                    <span className="xp-status">{status[c.status] ?? (state.kind === 'loading' ? 'Carregando…' : ' ')}</span>
                    <span className="xp-open" aria-hidden="true">Abrir →</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </section>
  );
}
